import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Paper,
} from "@mui/material";
import { useState, useEffect } from "react";
import { isValidJson, identifyTopicType } from "../../../utils/mqttUtils";
import { DoorData } from "./DoorSettingsPage";

import {
  connectToCloudServer,
  mqttClient,
  handleReceivedMessage,
} from "../../../utils/mqttUtils";

// アイコン
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import ZoomOutMapRoundedIcon from "@mui/icons-material/ZoomOutMapRounded";
import MeetingRoomRoundedIcon from "@mui/icons-material/MeetingRoomRounded";
import DoorSlidingRoundedIcon from "@mui/icons-material/DoorSlidingRounded";
import ZoomInMapRoundedIcon from "@mui/icons-material/ZoomInMapRounded";
import NoMeetingRoomRoundedIcon from "@mui/icons-material/NoMeetingRoomRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";

interface DoorInfoProps {
  doorData: DoorData | undefined;
  setDoorData: React.Dispatch<React.SetStateAction<any>>;
}

interface IconLayoutProps {
  icon: JSX.Element;
  text: string;
}

type MessageType = {
  ts: string;
  lock_sense: boolean;
  // その他の必要なプロパティもここに追加できます
} | undefined;

// Haudi標準MQTTメッセージ用定数
const haudiTopic1 = "haudi";
const haudiTopic2 = "dc2.0";

const IconLayout: React.FC<IconLayoutProps> = ({ icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", margin: 1 }}>
    {icon}
    <Typography variant="body1" sx={{ marginLeft: 1 }}>
      {text}
    </Typography>
  </Box>
);

const DoorInfo: React.FC<DoorInfoProps> = ({ doorData, setDoorData }) => {
  const [keyIcon, setKeyIcon] = useState<any>(
    <Box sx={{ display: "flex", alignItems: "center", margin: 1 }}>
      <CloudOffRoundedIcon />
      <Typography variant="body1" sx={{ marginLeft: 1 }}>
        データ取得中
      </Typography>
    </Box>,
  );
  const [doorIcon, setDoorIcon] = useState<any>(
    <Box sx={{ display: "flex", alignItems: "center", margin: 1 }}>
      <CloudOffRoundedIcon />
      <Typography variant="body1" sx={{ marginLeft: 1 }}>
        データ取得中
      </Typography>
    </Box>,
  );

  // 最後に受信したメッセージのタイムスタンプを保存
  const [lastReceivedTimestamp, setLastReceivedTimestamp] =
    useState<Date | undefined>(undefined);

  const [latestMessage, setLatestMessage] = useState<MessageType>(undefined);

  const parseTimestamp = (ts: string): Date => {
    // "2023-09-28T13:35:09.0+0900" から "2023-09-28T13:35:09+0900" に変換
    const sanitizedTs = ts.replace(/\.0/g, "");
    return new Date(sanitizedTs);
  };

  // 再接続関数
  const reconnectToMqtt = () => {
    if (mqttClient) {
      if (!mqttClient.connected) {
        if (doorData?.sdcUuid) {
          console.log("MQTTをリコネクトします。");
          connectToCloudServer(doorData.sdcUuid);
        }
      }
    } else {
      console.error("mqttClient is undefined");
    }
  };

  useEffect(() => {
    if (doorData?.sdcUuid) {
      connectToCloudServer(doorData.sdcUuid);
    }
    if (mqttClient) {
      mqttClient.on("message", (topic: any, message: any) => {
        console.log("mqttClient(CloudServer).on('message').");
        let messageString = message.toString();
        if (messageString === "") messageString = "{}";
        if (!isValidJson(messageString)) {
          console.error(
            "mqttClient.on('message') error - 受け取ったメッセージがJSON形式ではありません。",
          );
          return;
        }
        const jsonObj = JSON.parse(messageString);
        console.log(
          "handleReceivedMessage - メッセージ受信: トピック[" +
          topic +
          "], メッセージ[" +
          JSON.stringify(jsonObj) +
          "].",
        );

        const topicType = identifyTopicType(topic);
        if (topicType === "noMatch") {
          console.error(
            "handleReceivedMessage error - トピックが'haudi'と一致しません。",
          );
          return;
        }

        let newTopicMessages: [string, any][] = [];
        switch (topicType) {
          case haudiTopic1:
            newTopicMessages.push([topic, message]);
            break;
        }

        newTopicMessages.forEach((message) => {
          handleReceivedMessage(message[0], message[1]);
        });

        // IoTCoreでメッセージの順番が入れ替わってしまう現象をフロントで対応
        // メッセージを最新のメッセージとして保存
        setLatestMessage(jsonObj);

        switch (jsonObj.state) {
          case "opening":
            setDoorIcon(
              <IconLayout icon={<ZoomOutMapRoundedIcon />} text="開動作中" />,
            );
            break;
          case "opened":
            setDoorIcon(
              <IconLayout
                icon={<MeetingRoomRoundedIcon />}
                text="扉は開いています"
              />,
            );
            break;
          case "closing":
            setDoorIcon(
              <IconLayout icon={<ZoomInMapRoundedIcon />} text="閉動作中" />,
            );
            break;
          case "closed":
            setDoorIcon(
              <IconLayout
                icon={<DoorSlidingRoundedIcon />}
                text="扉は閉まっています"
              />,
            );
            break;
        }
      });
    } else {
      console.error("mqttClient is undefined");
    }

    // 5分ごとに再接続を試みる
    const interval = setInterval(
      () => {
        reconnectToMqtt();
      },
      5 * 60 * 1000,
    ); // 5 minutes in milliseconds

    // クリーンアップ関数：コンポーネントがアンマウントされるときにインターバルをクリアする
    return () => clearInterval(interval);
  }, [doorData?.sdcUuid]);

  // IoTCoreでメッセージの順番が入れ替わってしまう現象をフロントで対応
  useEffect(() => {
    if (latestMessage) {
      const currentTimestamp = new Date(latestMessage.ts);
      console.log("最新のTS" + currentTimestamp);
      if (lastReceivedTimestamp) {
        console.log("最後のTS" + lastReceivedTimestamp);
        if (currentTimestamp > lastReceivedTimestamp) {
          // 新しいメッセージを受信
          setKeyIcon(
            latestMessage.lock_sense ? (
              <IconLayout icon={<LockIcon />} text="施錠されています" />
            ) : (
              <IconLayout icon={<LockOpenIcon />} text="解錠されています" />
            ),
          );
        } else if (
          currentTimestamp.getTime() === lastReceivedTimestamp.getTime()
        ) {
          // 同じタイムスタンプのメッセージを受信
          if (latestMessage.lock_sense) {
            setKeyIcon(
              <IconLayout icon={<LockIcon />} text="施錠されています" />,
            );
          }
        }
      } else {
        // 最初のメッセージを受信
        setKeyIcon(
          latestMessage.lock_sense ? (
            <IconLayout icon={<LockIcon />} text="施錠されています" />
          ) : (
            <IconLayout icon={<LockOpenIcon />} text="解錠されています" />
          ),
        );
      }
      // lastReceivedTimestampを更新
      setLastReceivedTimestamp(currentTimestamp);
    }
  }, [latestMessage]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDoorData({ ...doorData, isEnabled: event.target.value === "true" });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row" colSpan={2}>
                  <Typography variant="h6">ドア情報</Typography>
                </TableCell>
              </TableRow>
              {doorData ? (
                <>
                  <TableRow>
                    <TableCell>SDCSN:</TableCell>
                    <TableCell>
                      <Typography>{doorData.sdcSerialNumber}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>施錠状態:</TableCell>
                    <TableCell>
                      <Typography>{keyIcon}</Typography>
                    </TableCell>
                  </TableRow>
                  {/* <TableRow>
                    <TableCell>動作状態:</TableCell>
                    <TableCell>
                      <Typography>{doorIcon}</Typography>
                    </TableCell>
                  </TableRow> */}
                  <TableRow>
                    <TableCell>取付先名（物件名称）:</TableCell>
                    <TableCell>
                      <Typography>{doorData.building.buildingName}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>住所:</TableCell>
                    <TableCell>
                      <Typography>{doorData.doorPlace}</Typography>
                    </TableCell>
                  </TableRow>
                  {/* <TableRow>
                    <TableCell>有効無効:</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FormControlLabel
                          value="true"
                          control={
                            <Radio
                              checked={doorData.isEnabled === true}
                              onChange={handleChange}
                              color="primary"
                            />
                          }
                          label="有効"
                        />
                        <FormControlLabel
                          value="false"
                          control={
                            <Radio
                              checked={doorData.isEnabled === false}
                              onChange={handleChange}
                              color="primary"
                            />
                          }
                          label="無効"
                        />
                      </Box>
                    </TableCell>
                  </TableRow> */}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography>データを取得中...</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default DoorInfo;
